import type { RGB } from './types/rgb.type';

/**
 * Parses an RGBA or RGB string and returns an object with the corresponding color channels.
 *
 * The input string should be in the format `rgba(r,g,b,a)` or `rgb(r,g,b)`, where:
 * - `r`, `g`, `b` are integers representing the red, green, and blue channels (0-255).
 * - `a` is an optional floating-point number representing the alpha channel (0-1). If omitted, alpha defaults to 1.
 *
 * @param rgba - The RGBA or RGB string to parse.
 * @returns An object containing the color channels: `{ r, g, b, a }`.
 * @throws Will throw an error if the input string is not a valid RGBA or RGB format.
 */
export const parseRgbaString = (rgba: string): RGB => {
  const match = /^rgba?\((\d+),(\d+),(\d+)(?:,([0-9.]+))?\)$/i.exec(rgba.replace(/\s+/g, ''));

  if (match === null) {
    throw new Error(`Invalid RGBA string: ${rgba}`);
  }

  const [, redValue, greenValue, blueValue, alphaValue] = match;

  const red = Number(redValue);
  const green = Number(greenValue);
  const blue = Number(blueValue);
  const alpha = alphaValue !== undefined ? Number.parseFloat(alphaValue) : 1;

  return { alpha, blue, green, red };
};
